import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { readCachedSessionUser } from "./authSession.storage";
import type { AuthSessionUser } from "./authSession.types";
import { getToken } from "../../services/storageService";
import { hasAppLoggedOutFlag, redirectToLogin } from "../logout";
import { enforceLoggedOutWall, isAppSessionActive } from "./authGate";
import { isPostLoginGraceActive } from "./authSession.grace";
import { isAuthCallbackPath, isApplicationNotAssignedPath } from "./authSession.service";
import { isSsoPrefetchRequest } from "./ssoPrefetch";

const STORAGE_DEBOUNCE_MS = 150;

const AuthSessionContext = createContext<UseQueryResult<AuthSessionUser, Error> | null>(
  null,
);

/**
 * Session context backed by localStorage after first login.
 * Network session API runs once on /auth/callback — not on refresh.
 */
export const AuthSessionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const cachedUser = readCachedSessionUser();

  useEffect(() => {
    enforceLoggedOutWall();
  }, [location.pathname]);

  useEffect(() => {
    let storageDebounce: ReturnType<typeof setTimeout> | null = null;

    const ensureSignedIn = () => {
      if (isSsoPrefetchRequest()) return;
      if (hasAppLoggedOutFlag()) {
        enforceLoggedOutWall();
        return;
      }
      if (isAuthCallbackPath()) return;
      if (isApplicationNotAssignedPath()) return;
      if (isPostLoginGraceActive()) return;
      if (isAppSessionActive()) return;
      if (getToken()) return;
      if (readCachedSessionUser()) return;
      redirectToLogin();
    };

    const scheduleEnsureSignedIn = () => {
      if (storageDebounce) clearTimeout(storageDebounce);
      storageDebounce = setTimeout(() => {
        storageDebounce = null;
        ensureSignedIn();
      }, STORAGE_DEBOUNCE_MS);
    };

    const onStorage = (event: StorageEvent) => {
      if (
        event.key === null ||
        event.key === "token" ||
        event.key === "auth_user" ||
        event.key === "auth_me_response"
      ) {
        scheduleEnsureSignedIn();
      }
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", ensureSignedIn);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        ensureSignedIn();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      if (storageDebounce) clearTimeout(storageDebounce);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", ensureSignedIn);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  const query = useQuery({
    queryKey: ["auth", "session"],
    queryFn: async () => {
      const user = readCachedSessionUser();
      if (!user) {
        throw new Error("Session not in storage");
      }
      return user;
    },
    enabled: false,
    initialData: cachedUser ?? undefined,
    staleTime: Infinity,
  });

  const value = useMemo(() => query, [query]);

  return (
    <AuthSessionContext.Provider value={value}>{children}</AuthSessionContext.Provider>
  );
};

export function useAuthSession(): UseQueryResult<AuthSessionUser, Error> {
  const ctx = useContext(AuthSessionContext);
  if (!ctx) {
    throw new Error("useAuthSession must be used within AuthSessionProvider");
  }
  return ctx;
}
